/**
 * LeftSidebar Component - Booku V2
 *
 * Sidebar kiri dengan menu utama.
 * Perubahan dari sistem lama:
 * - Menu utama TIDAK expand nested submenu di sidebar
 * - Klik menu yang punya children → buka SubMenuBar (horizontal bar)
 * - Klik menu leaf → langsung buka halaman
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useAuthStore } from '@/modules/auth/authStore';
import { useAppStore } from '@/stores/appStore';
import { useAppTabsStore } from '@/stores/appTabsStore';
import { useSubMenuStore, useActiveMainMenuId } from '@/stores/subMenuStore';
import { getPageByPath } from './PageRegistry';
import {
  mainMenuItems,
  filterMainMenuItems,
  filterMenuConfigItems,
  type MainMenuItem,
} from '@/config/menuConfig';

interface LeftSidebarProps {
  collapsed: boolean;
}

export function LeftSidebar({ collapsed }: LeftSidebarProps) {
  const navigate = useNavigate();
  const {
    LevelUserAktif,
    sebagaiPerusahaanDagang,
    sebagaiPerusahaanManufaktur,
    sebagaiPerusahaanJasa,
  } = useAuthStore();
  const { JenisTahunBuku } = useAppStore();
  const { tabs, activeTabId, openTab } = useAppTabsStore();
  const { openSubMenu, closeSubMenu, isOpen: isSubMenuOpen } = useSubMenuStore();
  const activeMainMenuId = useActiveMainMenuId();

  // Jenis usaha untuk filtering
  const jenisUsaha = useMemo(
    () => ({
      dagang: sebagaiPerusahaanDagang,
      manufaktur: sebagaiPerusahaanManufaktur,
      jasa: sebagaiPerusahaanJasa,
    }),
    [sebagaiPerusahaanDagang, sebagaiPerusahaanManufaktur, sebagaiPerusahaanJasa]
  );

  const isTahunBukuNormal = JenisTahunBuku === 'NORMAL';

  // Filter menu berdasarkan level user, jenis usaha, dan jenis tahun buku
  const filteredMenuItems = useMemo(
    () => filterMainMenuItems(mainMenuItems, LevelUserAktif, jenisUsaha, isTahunBukuNormal),
    [LevelUserAktif, jenisUsaha, isTahunBukuNormal]
  );

  // Handle klik menu leaf - langsung buka halaman
  const handleLeafClick = (path: string) => {
    const page = getPageByPath(path);
    if (page) {
      openTab({
        id: page.id,
        label: page.label,
        path: page.path,
        icon: page.icon,
      });
      navigate(path);
    }
    // Tutup SubMenuBar jika terbuka
    closeSubMenu();
  };

  // Handle klik menu branch - buka SubMenuBar
  const handleBranchClick = (item: MainMenuItem) => {
    if (!item.children || item.children.length === 0) return;

    // Filter children berdasarkan access control
    const filteredChildren = filterMenuConfigItems(
      item.children,
      LevelUserAktif,
      jenisUsaha,
      isTahunBukuNormal
    );

    if (filteredChildren.length === 0) return;

    // Jika SubMenuBar sudah terbuka dengan menu yang sama, tutup
    if (isSubMenuOpen && activeMainMenuId === item.id) {
      closeSubMenu();
    } else {
      // Buka SubMenuBar dengan children dari menu ini
      openSubMenu(item.id, item.label, filteredChildren);
    }
  };

  // Handle klik menu item
  const handleMenuClick = (item: MainMenuItem) => {
    if (item.path) {
      // Menu leaf - langsung buka halaman
      handleLeafClick(item.path);
    } else if (item.children && item.children.length > 0) {
      // Menu branch - buka SubMenuBar
      handleBranchClick(item);
    }
  };

  // Check if menu item is active
  const isMenuActive = (item: MainMenuItem): boolean => {
    // Jika menu sedang dibuka di SubMenuBar, highlight
    if (isSubMenuOpen && activeMainMenuId === item.id) {
      return true;
    }

    // Jika menu leaf, cek apakah tab aktif
    if (item.path) {
      const page = getPageByPath(item.path);
      if (!page) return false;

      const activeTab = tabs.find((t) => t.id === activeTabId);
      return activeTab?.id === page.id;
    }

    return false;
  };

  // Render menu item
  const renderMenuItem = (item: MainMenuItem) => {
    const hasChildren = item.children && item.children.length > 0;
    const isActive = isMenuActive(item);

    return (
      <div
        key={item.id}
        className={`menu-item ${isActive ? 'active' : ''}`}
        onClick={() => handleMenuClick(item)}
        data-label={item.label}
      >
        <span className="menu-icon">{item.icon}</span>
        <span className="menu-label">{item.label}</span>
        {/* Arrow indicator untuk menu dengan children */}
        {hasChildren && !collapsed && (
          <ChevronRight
            size={16}
            className={`menu-arrow-icon ${isActive ? 'active' : ''}`}
          />
        )}
      </div>
    );
  };

  // Kelompokkan menu yang sudah difilter
  const menuBeranda = filteredMenuItems.filter((m) => m.id === 'beranda');
  const menuDataTransaksi = filteredMenuItems.filter(
    (m) =>
      m.id === 'data' ||
      m.id === 'data-awal' ||
      m.id === 'jurnal' ||
      m.id === 'buku-besar' ||
      m.id === 'transaksi' ||
      m.id === 'pembelian' ||
      m.id === 'penjualan' ||
      m.id === 'stock-opname' ||
      m.id === 'buku-pengawasan' ||
      m.id === 'manajemen-asset'
  );
  const menuLaporan = filteredMenuItems.filter(
    (m) => m.id === 'laporan' || m.id === 'pajak'
  );
  const menuSistem = filteredMenuItems.filter(
    (m) => m.id === 'tutup-buku-tahunan' || m.id === 'pengaturan'
  );

  return (
    <aside className={`sidebar-left ${collapsed ? 'collapsed' : ''}`}>
      <nav className="sidebar-nav">
        {menuBeranda.length > 0 && (
          <>
            <div className="sidebar-section-title">Menu Utama</div>
            {menuBeranda.map(renderMenuItem)}
          </>
        )}

        {menuDataTransaksi.length > 0 && (
          <>
            <div className="sidebar-section-title">Data & Transaksi</div>
            {menuDataTransaksi.map(renderMenuItem)}
          </>
        )}

        {menuLaporan.length > 0 && (
          <>
            <div className="sidebar-section-title">Pelaporan</div>
            {menuLaporan.map(renderMenuItem)}
          </>
        )}

        {menuSistem.length > 0 && (
          <>
            <div className="sidebar-section-title">Sistem</div>
            {menuSistem.map(renderMenuItem)}
          </>
        )}
      </nav>
    </aside>
  );
}
